/**
 * Stable per-tab identity.
 *
 * `sessionStorage` is scoped to a single browsing-context (tab), and crucially
 * it **survives a page reload (F5 / Ctrl+R) in the same tab** — unlike a
 * module-level variable, which is recreated whenever the JS bundle is parsed
 * anew. That property is what lets us recognise our own previous lock after
 * a refresh and take it over, instead of waiting ~10 s for the reaper.
 *
 * Identity rules:
 *   - First call in a tab: generate `crypto.randomUUID()`, write to sessionStorage.
 *   - Subsequent calls (including after F5): read the same value back.
 *   - New tab / new browser profile: gets its own UUID — sessionStorage is
 *     not shared across tabs.
 *   - Storage unavailable (privacy mode, SSR, sandboxed iframe): degrade to
 *     a per-process in-memory id. This breaks refresh-survival but keeps the
 *     rest of the system working.
 *
 * The value is also memoised in-module so repeated calls don't churn through
 * sessionStorage on hot paths.
 */
export const TAB_ID_STORAGE_KEY = 'evse3:tabId';

let memoized: string | null = null;

function readSessionStorage(): string | null {
  try {
    if (typeof globalThis === 'undefined' || globalThis.sessionStorage == null) return null;
    return globalThis.sessionStorage.getItem(TAB_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeSessionStorage(value: string): void {
  try {
    if (typeof globalThis === 'undefined' || globalThis.sessionStorage == null) return;
    globalThis.sessionStorage.setItem(TAB_ID_STORAGE_KEY, value);
  } catch {
    /* storage quota / privacy mode — we still return a usable id from memory */
  }
}

function generateUuid(): string {
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  // Non-cryptographic fallback for environments without crypto.randomUUID
  // (rare in our target browsers; mostly here for tests/SSR sanity).
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Returns the stable tabId for the current browsing context, creating one
 * on the first call. Safe to call from any module at any time.
 */
export function getOrCreateTabId(): string {
  if (memoized !== null) return memoized;
  const fromStorage = readSessionStorage();
  if (fromStorage !== null && fromStorage.length > 0) {
    memoized = fromStorage;
    return memoized;
  }
  const fresh = generateUuid();
  writeSessionStorage(fresh);
  memoized = fresh;
  return memoized;
}

/**
 * Test-only: forget the memoised value so the next `getOrCreateTabId()` call
 * re-reads sessionStorage. Production code must never need this.
 */
export function __resetTabIdForTests(): void {
  memoized = null;
}
